import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createInvoice, updateInvoice, getInvoice, getInvoiceUsage } from '../redux/slices/invoiceSlice';
import UsageMeter from '../components/UsageMeter';
import { generateInvoicePdf, markPdfDownloaded } from '../utils/generateInvoicePdf';
import { getWatermarkText } from '../redux/slices/settingsSlice';
import { getMySubscription } from '../redux/slices/subscriptionSlice';
import { Plus, Trash2, Save, Upload, X, Image as ImageIcon, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import ClientSelector from '../components/ClientSelector';
import SaveClientModal from '../components/SaveClientModal';

const InvoiceBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentInvoice, isLoading, error, usage } = useSelector((state) => state.invoice);
  const { user } = useSelector((state) => state.auth);
  const { subscription } = useSelector((state) => state.subscription);
  const { watermarkText } = useSelector((state) => state.settings);

  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';
  const BASE_URL = API_URL.replace('/api', '');

  const [formData, setFormData] = useState({
    client_info: {
      name: '',
      email: '',
      company: '',
      address: '',
      phone: '',
    },
    shipTo: {
      name: '',
      email: '',
      company: '',
      address: '',
      phone: '',
      bankInfo: '',
    },
    billFrom: {
      name: '',
      email: '',
      company: '',
      address: '',
      phone: '',
      logo: '',
    },
    items: [{ description: '', quantity: 1, price: 0, total: 0 }],
    tax: { rate: 0, amount: 0 },
    discount: 0,
    shipping: 0,
    amountPaid: 0,
    balanceDue: 0,
    notes: '',
    terms: '',
    paymentTerms: '',
    poNumber: '',
    status: 'draft',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    currency: 'USD',
  });

  useEffect(() => {
    if (id) {
      dispatch(getInvoice(id));
    }
    dispatch(getWatermarkText());
    dispatch(getMySubscription());
    dispatch(getInvoiceUsage());
  }, [id, dispatch]);

  // Populate user data when user becomes available (for new invoices)
  useEffect(() => {
    if (user && !id) {
      setFormData(prev => ({
        ...prev,
        billFrom: {
          ...prev.billFrom,
          name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || '',
          email: user.email || '',
          company: user.profile?.company || '',
          phone: user.profile?.phone || '',
        }
      }));
    }
  }, [user, id]);


  useEffect(() => {
    if (currentInvoice && id) {
      try {
        
        setFormData({
          ...currentInvoice,
          // Ensure all nested objects exist with default values
          client_info: {
            name: '',
            email: '',
            company: '',
            address: '',
            phone: '',
            ...currentInvoice.client_info
          },
          shipTo: {
            name: '',
            email: '',
            company: '',
            address: '',
            phone: '',
            bankInfo: '',
            ...currentInvoice.shipTo
          },
          billFrom: {
            name: '',
            email: '',
            company: '',
            address: '',
            phone: '',
            logo: '',
            ...(currentInvoice.billFrom || {})
          },
          items: currentInvoice.items || [{ description: '', quantity: 1, price: 0, total: 0 }],
          tax: {
            rate: 0,
            amount: 0,
            ...currentInvoice.tax
          },
          issueDate: currentInvoice.issueDate 
            ? new Date(currentInvoice.issueDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          dueDate: currentInvoice.dueDate 
            ? new Date(currentInvoice.dueDate).toISOString().split('T')[0]
            : '',
          // Ensure other fields have defaults
          discount: currentInvoice.discount || 0,
          shipping: currentInvoice.shipping || 0,
          amountPaid: currentInvoice.amountPaid || 0,
          balanceDue: currentInvoice.balanceDue || 0,
          notes: currentInvoice.notes || '',
          terms: currentInvoice.terms || '',
          paymentTerms: currentInvoice.paymentTerms || '',
          poNumber: currentInvoice.poNumber || '',
          currency: currentInvoice.currency || 'USD',
        });
      } catch (error) {
        toast.error('Error loading invoice data');
      }
    }
  }, [currentInvoice, id]);

  const handleClientChange = (field, value) => {
    setFormData({
      ...formData,
      client_info: { ...formData.client_info, [field]: value },
    });
  };

  const handleClientSelect = (clientData) => {
    setFormData(prev => ({
      ...prev,
      client_info: {
        ...prev.client_info,
        ...clientData
      }
    }));
    setShowClientSelector(false);
  };

  const handleBillFromChange = (field, value) => {
    setFormData({
      ...formData,
      billFrom: { ...formData.billFrom, [field]: value },
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;

    if (field === 'quantity' || field === 'price') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].price;
    }

    setFormData({ ...formData, items: updatedItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, price: 0, total: 0 }],
    });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showSaveClientModal, setShowSaveClientModal] = useState(false);

  const handleLogoSelection = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSelectedLogoFile(file);
    setLogoPreview(previewUrl);
    toast.success('Logo selected. Click "Upload Logo" to confirm.');
  };


  const handleLogoUpload = async () => {
    if (!selectedLogoFile) {
      toast.error('Please select a logo file first');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('logo', selectedLogoFile);

    try {
      const response = await fetch(`${API_URL}/upload/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadFormData
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          billFrom: {
            ...prev.billFrom,
            logo: data.logoUrl
          }
        }));
        setSelectedLogoFile(null);
        setLogoPreview(null);
        toast.success('Logo uploaded successfully');
        
        // Auto-save the logo to the invoice if we're editing an existing invoice
        if (id) {
          try {
            const updatedFormData = {
              ...formData,
              billFrom: {
                ...formData.billFrom,
                logo: data.logoUrl
              }
            };
            await dispatch(updateInvoice({ id, data: updatedFormData }));
          } catch (error) {
            // Don't show error to user as the logo is still uploaded successfully
          }
        }
      } else {
        toast.error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo: ' + error.message);
    }
  };

  const cancelLogoSelection = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setSelectedLogoFile(null);
    setLogoPreview(null);
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      billFrom: {
        ...prev.billFrom,
        logo: ''
      }
    }));
  };

  const calculateTotals = () => {
    const items = formData.items || [];
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxRate = formData.tax?.rate || 0;
    const taxAmount = (subtotal * taxRate) / 100;
    const discount = formData.discount || 0;
    const shipping = formData.shipping || 0;
    const amountPaid = formData.amountPaid || 0;
    const total = subtotal + taxAmount - discount + shipping;
    const balanceDue = total - amountPaid;
    return { subtotal, taxAmount, total, balanceDue };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_info.name) {
      toast.error('Please enter client name');
      return;
    }

    if (formData.items.length === 0 || !formData.items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    if (!id && usage?.atCap) {
      toast.error(usage.limit
        ? `You've reached your monthly limit of ${usage.limit} invoices. Upgrade your plan to create more invoices.`
        : 'You have reached your monthly invoice limit. Upgrade to create more invoices.');
      return;
    }

    const { subtotal, taxAmount, total, balanceDue } = calculateTotals();

    const invoiceData = {
      ...formData,
      subtotal,
      tax: { ...formData.tax, amount: taxAmount },
      total,
      balanceDue,
    };


    try {
      if (id) {
        await dispatch(updateInvoice({ id, data: invoiceData })).unwrap();
        toast.success('Invoice updated successfully');
      } else {
        await dispatch(createInvoice(invoiceData)).unwrap();
        toast.success('Invoice created successfully');
        dispatch(getInvoiceUsage());
      }
      navigate('/invoices');
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to save invoice');
    }
  };

  const downloadPDF = async () => {
    try {
      const totals = calculateTotals();
      await generateInvoicePdf({
        ...formData,
        subtotal: totals.subtotal,
        tax: { ...formData.tax, amount: totals.taxAmount },
        total: totals.total,
        balanceDue: totals.balanceDue,
      }, {
        customBranding: !!subscription?.plan_id?.features?.customBranding,
        apiUrl: API_URL,
      });
      markPdfDownloaded(user?._id || user?.id);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const { subtotal, taxAmount, total, balanceDue } = calculateTotals();
  // Show loading state
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading invoice...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">Error loading invoice: {error}</p>
            <button 
              onClick={() => navigate('/invoices')} 
              className="btn btn-primary"
            >
              Back to Invoices
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
        <div className="flex space-x-2">
          <button type="button" onClick={downloadPDF} className="btn btn-secondary">
            Download PDF
          </button>
          {!id && usage?.atCap ? (
            <Link to="/subscriptions" className="btn btn-primary">
              Upgrade
            </Link>
          ) : (
            <button onClick={handleSubmit} className="btn btn-primary flex items-center space-x-2">
              <Save className="w-5 h-5" />
              <span>Save Invoice</span>
            </button>
          )}
        </div>
      </div>

      <UsageMeter usage={usage} />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bill From & Bill To */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Bill From</h3>
            
            {/* Logo Upload Section */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Company Logo</label>
                {subscription?.plan_id?.features?.customBranding ? (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    ✓ Pro Feature
                  </span>
                ) : (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    Pro Plan Required
                  </span>
                )}
              </div>
              {formData.billFrom.logo ? (
                <div className="relative inline-block">
                  <img 
                    src={`${API_URL}/upload/logo/${formData.billFrom.logo.split('/').pop()}`} 
                    alt="Company Logo" 
                    className="w-20 h-20 object-contain border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : logoPreview ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      className="w-20 h-20 object-contain border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLogoUpload();
                      }}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Upload Logo
                    </button>
                    <button
                      type="button"
                      onClick={cancelLogoSelection}
                      className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                  subscription?.plan_id?.features?.customBranding 
                    ? 'border-gray-300' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelection}
                    className="hidden"
                    id="logo-upload"
                    disabled={!subscription?.plan_id?.features?.customBranding}
                  />
                  <label 
                    htmlFor="logo-upload" 
                    className={`${
                      subscription?.plan_id?.features?.customBranding 
                        ? 'cursor-pointer' 
                        : 'cursor-not-allowed'
                    }`}
                  >
                    <ImageIcon className={`w-8 h-8 mx-auto mb-2 ${
                      subscription?.plan_id?.features?.customBranding 
                        ? 'text-gray-400' 
                        : 'text-gray-300'
                    }`} />
                    <p className={`text-sm ${
                      subscription?.plan_id?.features?.customBranding 
                        ? 'text-gray-600' 
                        : 'text-gray-400'
                    }`}>
                      {subscription?.plan_id?.features?.customBranding 
                        ? 'Click to select logo' 
                        : 'Upgrade to Pro to add logo'}
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  </label>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                className="input text-sm"
                value={formData.billFrom.name}
                onChange={(e) => handleBillFromChange('name', e.target.value)}
              />
              <input
                type="email"
                placeholder="your@email.com"
                className="input text-sm"
                value={formData.billFrom.email}
                onChange={(e) => handleBillFromChange('email', e.target.value)}
              />
              <input
                type="text"
                placeholder="Company"
                className="input text-sm"
                value={formData.billFrom.company}
                onChange={(e) => handleBillFromChange('company', e.target.value)}
              />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Bill To</h3>
              {user?.subscription?.plan_id?.features?.clientManagement && (
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowClientSelector(true)}
                    className="btn btn-sm btn-outline flex items-center space-x-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>Select Client</span>
                  </button>
                  {formData.client_info.name && (
                    <button
                      type="button"
                      onClick={() => setShowSaveClientModal(true)}
                      className="btn btn-sm btn-primary flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save as Client</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Client Name *"
                className="input text-sm"
                value={formData.client_info.name}
                onChange={(e) => handleClientChange('name', e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="client@email.com"
                className="input text-sm"
                value={formData.client_info.email}
                onChange={(e) => handleClientChange('email', e.target.value)}
              />
              <input
                type="text"
                placeholder="Company"
                className="input text-sm"
                value={formData.client_info.company}
                onChange={(e) => handleClientChange('company', e.target.value)}
              />
              <textarea
                placeholder="Address (Street, City, State, ZIP, Country)"
                className="input text-sm resize-none"
                rows="3"
                value={formData.client_info.address}
                onChange={(e) => handleClientChange('address', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Ship To (Optional) */}
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-4">Ship To (Optional)</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Name"
                className="input text-sm"
                value={formData.shipTo?.name || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shipTo: { ...(formData.shipTo || {}), name: e.target.value } 
                })}
              />
              <input
                type="email"
                placeholder="Email"
                className="input text-sm"
                value={formData.shipTo?.email || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shipTo: { ...(formData.shipTo || {}), email: e.target.value } 
                })}
              />
              <input
                type="text"
                placeholder="Company"
                className="input text-sm"
                value={formData.shipTo?.company || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shipTo: { ...(formData.shipTo || {}), company: e.target.value } 
                })}
              />
              <input
                type="text"
                placeholder="Phone"
                className="input text-sm"
                value={formData.shipTo?.phone || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  shipTo: { ...(formData.shipTo || {}), phone: e.target.value } 
                })}
              />
            </div>
            <textarea
              placeholder="Shipping Address (Street, City, State, ZIP, Country)"
              className="input text-sm resize-none"
              rows="3"
              value={formData.shipTo?.address || ''}
              onChange={(e) => setFormData({
                ...formData,
                shipTo: { ...(formData.shipTo || {}), address: e.target.value } 
              })}
            />
            <textarea
              placeholder="Bank Information (Account details, SWIFT, IBAN, etc.)"
              className="input text-sm resize-none"
              rows="2"
              value={formData.shipTo?.bankInfo || ''}
              onChange={(e) => setFormData({
                ...formData,
                shipTo: { ...(formData.shipTo || {}), bankInfo: e.target.value }
              })}
            />
          </div>
        </div>

        {/* Invoice Details */}
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-4">Invoice Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Issue Date</label>
              <input
                type="date"
                className="input"
                value={formData.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
              <input
                type="date"
                className="input"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
              <input
                type="text"
                placeholder="e.g., Net 30"
                className="input"
                value={formData.paymentTerms}
                onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
              <input
                type="text"
                placeholder="Purchase Order #"
                className="input"
                value={formData.poNumber}
                onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Items</h3>
            <button type="button" onClick={addItem} className="btn btn-secondary btn-sm flex items-center space-x-1">
              <Plus className="w-4 h-4" />
              <span>Add Item</span>
            </button>
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-center">
                <input
                  type="text"
                  placeholder="Description"
                  className="input col-span-5"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Qty"
                  className="input col-span-2"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                  min="1"
                />
                <input
                  type="number"
                  placeholder="Price"
                  className="input col-span-2"
                  value={item.price}
                  onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
                <div className="col-span-2 font-medium">${item.total.toFixed(2)}</div>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="col-span-1 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-6 pt-6 border-t">
            <div className="space-y-2 max-w-xs ml-auto">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Tax (%):</span>
                <input
                  type="number"
                  className="input w-20 text-sm"
                  value={formData.tax.rate}
                  onChange={(e) => setFormData({
                    ...formData,
                    tax: { ...formData.tax, rate: parseFloat(e.target.value) || 0 }
                  })}
                  min="0"
                  step="0.1"
                />
                <span className="font-medium">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Discount:</span>
                <input
                  type="number"
                  className="input w-32 text-sm"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Shipping:</span>
                <input
                  type="number"
                  className="input w-32 text-sm"
                  value={formData.shipping}
                  onChange={(e) => setFormData({ ...formData, shipping: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Amount Paid:</span>
                <input
                  type="number"
                  className="input w-32 text-sm"
                  value={formData.amountPaid}
                  onChange={(e) => setFormData({ ...formData, amountPaid: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span>Balance Due:</span>
                <span>${balanceDue.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes & Terms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              className="input"
              rows="4"
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions</label>
            <textarea
              className="input"
              rows="4"
              placeholder="Payment terms..."
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
            />
          </div>
        </div>
      </form>

      {/* Client Selector Modal */}
      {showClientSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative">
            <ClientSelector
              onClientSelect={handleClientSelect}
              currentClient={formData.client_info}
              onClose={() => setShowClientSelector(false)}
            />
          </div>
        </div>
      )}

      {/* Save Client Modal */}
      {showSaveClientModal && (
        <SaveClientModal
          clientData={formData.client_info}
          onSave={() => {
            // Refresh client list or show success message
            toast.success('Client saved! You can now select them from the client list.');
          }}
          onClose={() => setShowSaveClientModal(false)}
        />
      )}
    </div>
  );
};

export default InvoiceBuilder;

